import { useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { MapBounds } from "../../utils/mapBounds";

/** `/dashboard?map=fullscreen` opens the asset-locations map full screen. */
export const MAP_FULLSCREEN_PARAM = "map";
export const MAP_FULLSCREEN_VALUE = "fullscreen";

interface FullscreenLocationState {
  /** Set when the map was opened from the widget; null when the widget had no view yet. */
  mapFullscreenView?: MapBounds | null;
}

/**
 * Open/close state of the full-screen map, kept in the URL so the view is linkable and
 * the browser's back button closes it. Opening from the widget pushes a history entry
 * (with the widget's view in the location state), so closing goes back to it; a direct
 * link has no entry to go back to, so closing just drops the parameter.
 */
export const useAssetMapFullscreen = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as FullscreenLocationState | null;

  const isOpen = searchParams.get(MAP_FULLSCREEN_PARAM) === MAP_FULLSCREEN_VALUE;
  const openedInApp = state != null && "mapFullscreenView" in state;

  const open = useCallback((viewBounds?: MapBounds) => {
    const params = new URLSearchParams(location.search);
    params.set(MAP_FULLSCREEN_PARAM, MAP_FULLSCREEN_VALUE);
    navigate({ search: params.toString() }, { state: { mapFullscreenView: viewBounds ?? null } });
  }, [location.search, navigate]);

  const close = useCallback(() => {
    if (openedInApp) {
      navigate(-1);
      return;
    }
    const params = new URLSearchParams(location.search);
    params.delete(MAP_FULLSCREEN_PARAM);
    navigate({ search: params.toString() }, { replace: true });
  }, [openedInApp, location.search, navigate]);

  return {
    isOpen,
    viewBounds: state?.mapFullscreenView ?? undefined,
    open,
    close,
  };
};
